import { CARD_SECTIONS, type BoardDto, type CardDto, type CardSection, type MoveCardDto } from './types';

/** Droppable id for one section of one stream: `"streamId:section"`. Card ids are numbers. */
export function zoneId(streamId: number, section: CardSection): string {
  return `${streamId}:${section}`;
}

/** Parses a zone id back into its parts; returns null for anything that is not a zone id. */
export function parseZoneId(id: string | number): { workStreamId: number; section: CardSection } | null {
  if (typeof id !== 'string') return null;
  const [streamPart, sectionPart] = id.split(':');
  const workStreamId = Number(streamPart);
  if (!Number.isFinite(workStreamId) || !CARD_SECTIONS.includes(sectionPart as CardSection)) return null;
  return { workStreamId, section: sectionPart as CardSection };
}

function allCards(board: BoardDto): CardDto[] {
  return board.streams.flatMap((s) => s.cards);
}

/** Cards of one (stream, section), in sortOrder. */
export function sectionCards(cards: CardDto[], workStreamId: number, section: CardSection): CardDto[] {
  return cards
    .filter((c) => c.workStreamId === workStreamId && c.section === section)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Resolves a drop into the `MoveCardDto` body. `sortOrder` is the 0-based index the card ends
 * up at in the destination section, i.e. the insert index into that section after the dragged
 * card has been removed (the API contract).
 *
 * - `overId` is a zone id → append to that section.
 * - `overId` is a card id → take that card's position. Within the same section this is the
 *   over card's current index (what the sortable preview shows: dropping on the next card moves
 *   down one); across sections it is the over card's index (lands before it), or index + 1
 *   when `belowOverCentre` is true (the dragged card's centre is below the over card's centre).
 *
 * Returns null when the drop is not a move (unknown ids, or the card would not change place).
 */
export function resolveMove(
  board: BoardDto,
  cardId: number,
  overId: string | number | null | undefined,
  belowOverCentre = false,
): MoveCardDto | null {
  if (overId === null || overId === undefined) return null;
  const cards = allCards(board);
  const card = cards.find((c) => c.id === cardId);
  if (!card) return null;

  let target: MoveCardDto;
  const zone = parseZoneId(overId);
  if (zone) {
    const remaining = sectionCards(cards, zone.workStreamId, zone.section).filter((c) => c.id !== cardId);
    target = { ...zone, sortOrder: remaining.length };
  } else {
    const overCard = cards.find((c) => c.id === overId);
    if (!overCard || overCard.id === cardId) return null;
    const destination = sectionCards(cards, overCard.workStreamId, overCard.section);
    const overIndex = destination.findIndex((c) => c.id === overCard.id);
    const crossSection = overCard.workStreamId !== card.workStreamId || overCard.section !== card.section;
    target = {
      workStreamId: overCard.workStreamId,
      section: overCard.section,
      sortOrder: crossSection && belowOverCentre ? overIndex + 1 : overIndex,
    };
  }

  const sameSection = target.workStreamId === card.workStreamId && target.section === card.section;
  if (sameSection) {
    const currentIndex = sectionCards(cards, card.workStreamId, card.section).findIndex((c) => c.id === cardId);
    if (currentIndex === target.sortOrder) return null;
  }
  return target;
}

/**
 * Returns a copy of the board with the move applied (for the optimistic cache update). Source
 * and destination sections are renumbered 0..n, mirroring the server. Header counts
 * (`usedWorkers`) are server-derived and deliberately not recomputed here; the refetch after the move updates them.
 */
export function applyMove(board: BoardDto, cardId: number, move: MoveCardDto): BoardDto {
  const cards = allCards(board);
  const card = cards.find((c) => c.id === cardId);
  if (!card) return board;

  const others = cards.filter((c) => c.id !== cardId);
  const source = sectionCards(others, card.workStreamId, card.section);
  const destination = sectionCards(others, move.workStreamId, move.section);
  const index = Math.max(0, Math.min(move.sortOrder, destination.length));
  destination.splice(index, 0, { ...card, workStreamId: move.workStreamId, section: move.section });

  const renumbered = new Map<number, CardDto>();
  for (const list of [source, destination]) {
    list.forEach((c, i) => renumbered.set(c.id, { ...c, sortOrder: i }));
  }
  const next = [...others.filter((c) => !renumbered.has(c.id)), ...renumbered.values()];

  return {
    ...board,
    streams: board.streams.map((s) => ({
      ...s,
      cards: next
        .filter((c) => c.workStreamId === s.id)
        .sort(
          (a, b) =>
            CARD_SECTIONS.indexOf(a.section) - CARD_SECTIONS.indexOf(b.section) || a.sortOrder - b.sortOrder,
        ),
    })),
  };
}
